import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Link } from 'react-router-dom';
import { Stack, Tabs, TabsList, TabsTrigger, TabsContent, Text } from '@nike/podium';
import { CategorySection, DetailAccordion, DetailSection, HighlightedSection, StackedDetail } from './EntityOverviewComponents';
import { GenderFitVariations } from './GenderFitVariations';
import { getFieldLabel } from '~/constants/projectFieldConfig';
import { ExpressionMiniCard } from '~/components/cardView/ExpressionMiniCard';
import { useProductInstances } from '~/atoms/hooks/useProductInstances';
const CUSTOM_LABELS = {
    track: 'Creation Track',
    creationTrack: 'Creation Track',
    creationTeam: 'Creation Team',
    intendedLifecycle: 'Intended Lifecycle',
    keyToolingBottom: 'Key Tooling',
    keyUpper: 'Key Upper',
    toolingRfc: 'Tooling RFC',
    upperRfc: 'Upper RFC',
    projectedUnit: 'Projected Units',
    intendedLaunch: 'Intended Launch',
    globalSportFocus: 'Global Sport Focus',
    globalSportSubFocus: 'Global Sport Sub Focus',
    subBrand: 'Sub Brand',
    fieldOfPlay: 'Field of Play',
    sportActivity: 'Sport Activity',
    strategicIntent: 'Strategic Intent',
    relatedStyles: 'Related Styles',
    targetGeo: 'Target Geo',
    newTool: 'New Tool And/Or',
    materialAndMins: 'Material & Mins',
    buildValue: 'Build Value',
    dutyDriver: 'Duty Driver',
    toolingBudget: 'Tooling Budget',
    tdCode: 'TD Code',
    sampleLast: 'Sample Last',
    testingSizes: 'Testing Size(s)'
};
const DETAILS_SECTION_CONFIG = [
    {
        id: 'product-details',
        title: 'Product Overview',
        fields: [
            'brand',
            'subBrand',
            'division',
            'fieldOfPlay',
            'sportActivity',
            'franchise',
            'silo',
            'segment',
            'strategicIntent',
            'creationTeam',
            'creationTrack',
            'track',
            'tier',
            'sustainabilityIntent'
        ]
    },
    {
        id: 'variations',
        title: 'Variations',
        fields: ['genderVariations', 'sizeVariations']
    },
    {
        id: 'technologies',
        title: 'Technologies & Materials',
        fields: [
            'technologies',
            'keyToolingBottom',
            'keyUpper',
            'toolingRfc',
            'upperRfc',
            'newTool',
            'materialAndMins'
        ]
    },
    {
        id: 'planning',
        title: 'Planning',
        fields: ['intendedLaunch', 'season', 'intendedLifecycle', 'projectedUnit']
    },
    {
        id: 'nuts-bolts',
        title: 'Nuts & Bolts',
        fields: [
            'targetGeo',
            'buildValue',
            'margin',
            'dutyDriver',
            'toolingBudget',
            'tdCode',
            'factory',
            'sampleLast',
            'testingSizes',
            'production'
        ],
        displayType: 'stacked'
    },
    {
        id: 'related',
        title: 'Related',
        fields: ['relatedStyles']
    }
];
const OVERVIEW_SECTIONS_OPEN = ['gender', 'insights', 'consumer', 'benefits'];
const DETAILS_SECTIONS_OPEN = DETAILS_SECTION_CONFIG.map((s) => s.id);
function InspirationElementDisplay({ elements }) {
    if (!elements || elements.length === 0) {
        return (_jsx(Text, { font: "body-2", className: "pds-color-text-secondary", children: "\u2014" }));
    }
    return (_jsx(Stack, { gap: "m", children: elements.map((element, index) => (_jsxs(Stack, { gap: "xs", children: [_jsx(Text, { font: "body-2-strong", children: element.name }), (element.details || []).map((detail, detailIndex) => (_jsx(Text, { font: "body-2", className: "pds-color-text-secondary", children: detail }, detailIndex)))] }, index))) }));
}
function ProjectOverviewContent({ project }) {
    const { data: allSeasons, isLoading: loadingSeasons, error: seasonsError } = useProductInstances(project.id, project.handle);
    const expressions = allSeasons.filter((season) => season.variants.length > 0);
    const SEASONS_SECTIONS_OPEN = expressions.length > 0 ? [expressions[0].id] : [];
    return (_jsxs(Stack, { gap: "m", className: "w-full", children: [_jsx(Link, { to: `/projects/detail/${project.handle || project.id}`, state: {
                    from: '/projects/view/card',
                    fromLabel: 'Back to Projects',
                    reopenSidebar: true,
                    sidebarEntity: project,
                    sidebarEntityType: 'project'
                }, className: "text-sm underline pds-color-text-primary hover:pds-color-text-secondary", children: "View Full Details" }), _jsxs(Tabs, { defaultValue: "overview", className: "w-full", children: [_jsxs(TabsList, { variant: "overlay", children: [_jsx(TabsTrigger, { value: "overview", children: "Overview" }), _jsx(TabsTrigger, { value: "details", children: "Details" }), _jsx(TabsTrigger, { value: "teams", children: "Teams" }), _jsx(TabsTrigger, { value: "seasons", children: "Seasons" })] }), _jsx(TabsContent, { value: "overview", children: _jsxs(Stack, { gap: "l", className: "w-full pt-4", children: [_jsx(HighlightedSection, { items: [
                                        { label: 'Segment', value: project.segment?.label },
                                        { label: 'Tier', value: project.tier?.label },
                                        { label: 'Silo', value: project.silo?.label }
                                    ] }), _jsx(HighlightedSection, { items: [
                                        { label: 'Lifecycle', value: project.intendedLifecycle?.label },
                                        { label: 'Launch', value: project.season?.[0]?.label },
                                        {
                                            label: 'MSRP',
                                            value: project.msrp ? `$${project.msrp}` : undefined
                                        }
                                    ] }), _jsxs(DetailAccordion, { defaultOpen: OVERVIEW_SECTIONS_OPEN, children: [_jsx(DetailSection, { value: "gender", title: "Gender & Fit Variations", children: _jsx(GenderFitVariations, { project: project }) }), _jsx(DetailSection, { value: "insights", title: getFieldLabel('insightElements'), children: _jsx(InspirationElementDisplay, { elements: project.insightElements || [] }) }), _jsx(DetailSection, { value: "consumer", title: getFieldLabel('consumerElements'), children: _jsx(InspirationElementDisplay, { elements: project.consumerElements || [] }) }), _jsx(DetailSection, { value: "benefits", title: getFieldLabel('benefitElements'), children: _jsx(InspirationElementDisplay, { elements: project.benefitElements || [] }) })] })] }) }), _jsx(TabsContent, { value: "details", children: _jsx(Stack, { gap: "l", className: "w-full pt-4", children: _jsx(DetailAccordion, { defaultOpen: DETAILS_SECTIONS_OPEN, children: DETAILS_SECTION_CONFIG.map((section) => (_jsx(CategorySection, { entity: project, section: section, customLabels: CUSTOM_LABELS }, section.id))) }) }) }), _jsx(TabsContent, { value: "teams", children: _jsxs(Stack, { gap: "l", className: "w-full pt-4", children: [project.teams && project.teams.length > 0 ? (_jsx(Stack, { gap: "m", children: project.teams.map((team) => (_jsxs(Stack, { gap: "xs", children: [_jsx(Text, { font: "body-2-strong", children: team.teamName }), _jsxs(Text, { font: "body-2", className: "pds-color-text-secondary", children: [team.members.length, " member(s)"] })] }, team.teamId))) })) : (_jsx(Text, { font: "body-2", className: "pds-color-text-secondary", children: "No teams assigned" })), project.criticalDateTimeline &&
                                    project.criticalDateTimeline.length > 0 && (_jsxs(Stack, { gap: "m", children: [_jsx(Text, { font: "body-2-strong", children: "Critical Date Timeline" }), project.criticalDateTimeline.map((entry, index) => (_jsxs(Stack, { gap: "xs", children: [_jsx(StackedDetail, { label: "Date", value: entry.date }), _jsx(StackedDetail, { label: "Task", value: entry.task }), _jsx(StackedDetail, { label: "Owner", value: entry.owner })] }, index)))] })), project.unansweredQuestions &&
                                    project.unansweredQuestions.length > 0 && (_jsxs(Stack, { gap: "m", children: [_jsx(Text, { font: "body-2-strong", children: "Unanswered Questions" }), project.unansweredQuestions.map((question, index) => (_jsxs(Text, { font: "body-2", className: "pds-color-text-secondary", children: ["\u2022 ", question] }, index)))] }))] }) }), _jsx(TabsContent, { value: "seasons", children: _jsx(Stack, { gap: "l", className: "w-full pt-4", children: loadingSeasons ? (_jsx(Text, { font: "body-2", className: "pds-color-text-secondary", children: "Loading seasons..." })) : seasonsError ? (_jsx(Text, { font: "body-2", className: "pds-color-text-error", children: "Failed to load seasons" })) : expressions.length > 0 ? (_jsx(DetailAccordion, { defaultOpen: SEASONS_SECTIONS_OPEN, children: expressions.map((season) => (_jsx(DetailSection, { value: season.id, title: season.name, children: _jsx(Stack, { children: season.variants.map((variant, index) => (_jsx(ExpressionMiniCard, { variant: variant, isFirst: index === 0 }, variant.id))) }) }, season.id))) })) : (_jsx(Text, { font: "body-2", className: "pds-color-text-secondary", children: "No seasonal variants available" })) }) })] })] }));
}
export default ProjectOverviewContent;
